import { canControlDelivery, deliveryControlCommand, deliveryControlCommands, deliveryStopped, hasHoldableState } from "../lib/artifactRolloutControls.js";
import { waveAdvanceCommands, waveProgress } from "../lib/artifactWaveProgress.js";

const AUTOMATIC = "ARTIFACT_WAVE_MODE_AUTOMATIC";
const DECLINED = "ARTIFACT_PUBLICATION_STATE_DECLINED";
const GENERAL = "ARTIFACT_AUDIENCE_KIND_GENERAL";
const MANUAL = "ARTIFACT_WAVE_MODE_MANUAL";
const PAUSED = "ARTIFACT_PUBLICATION_STATE_PAUSED";
const PENDING = "ARTIFACT_PUBLICATION_STATE_PENDING";
const ROLLING = "ARTIFACT_PUBLICATION_STATE_ROLLING";
const TENANT_MAIN = "ARTIFACT_AUDIENCE_KIND_TENANT_MAIN";
const TENANT_PREVIEW = "ARTIFACT_AUDIENCE_KIND_TENANT_PREVIEW";
const TENANT_TARGET = "ARTIFACT_AUDIENCE_KIND_TENANT_TARGET";

const AUDIENCES = {
    [GENERAL]: "Origin",
    [TENANT_MAIN]: "Main",
    [TENANT_PREVIEW]: "Preview",
    [TENANT_TARGET]: "Targeted",
};

function stateLabel(row) {
    if (row.publicationState === DECLINED) return "rejected";
    if (hasHoldableState(row) && row.held) return "held";
    if (row.awaitingGeneralCompletion) return "waiting for Origin";
    if (row.publicationState === PENDING && row.audienceKind === TENANT_MAIN) return "awaiting approval";
    return String(row.publicationState).replace("ARTIFACT_PUBLICATION_STATE_", "").toLowerCase();
}

export default function ContentUpdatesRolloutRow({ advancingDisabled, allRows, buttonClass, canControl, onRun, rows }) {
    const states = new Map();
    const waves = new Map();
    for (const row of rows) {
        const audience = AUDIENCES[row.audienceKind] ?? "Other";
        const key = `${audience}: ${stateLabel(row)}`;
        states.set(key, (states.get(key) ?? 0) + 1);
        const progress = waveProgress(row);
        if (progress.known || progress.complete) {
            const wave = progress.complete ? "waves completed" : `wave ${progress.currentWave} of ${progress.finalWave}, ${progress.completedWaves} completed`;
            const waveKey = `${audience}: ${wave}`;
            waves.set(waveKey, (waves.get(waveKey) ?? 0) + 1);
        }
    }
    const mutable = rows.filter((row) => !row.systemEnforced && row.audienceKind !== GENERAL);
    const latest = new Map();
    for (const row of allRows) {
        if (row.audienceKind !== TENANT_MAIN) continue;
        const previous = latest.get(row.componentId);
        if (!previous || Number(row.version) > Number(previous.version)) latest.set(row.componentId, row);
    }
    const startable = [...latest.values()].filter((row) =>
        [PENDING, DECLINED].includes(row.publicationState) && !row.awaitingGeneralCompletion && !row.systemEnforced,
    );
    const rolling = mutable.filter((row) => row.publicationState === ROLLING);
    const paused = mutable.filter((row) => row.publicationState === PAUSED);
    const holdable = mutable.filter(canControlDelivery);
    const manual = [...rolling, ...paused].filter((row) => row.waveMode === MANUAL);
    const action = (text, name, values, disabled = false) => values.length > 0 && (
        <button type="button" className={buttonClass} disabled={disabled} onClick={() => void onRun(name, values)}>{text}</button>
    );
    return (
        <tr data-content-updates>
            <td className="px-3 py-2 text-[color:var(--ink)]">Content updates</td>
            <td className="px-3 py-2 text-[color:var(--muted)]" colSpan="5">
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {[...states].sort(([left], [right]) => left.localeCompare(right)).map(([state, count]) => <span key={state}>{state} ({count})</span>)}
                </div>
                <p className="mt-1 text-[9px] text-[color:var(--muted-soft)]">Counts describe loaded updates. Manage the content schedule under Schedule and overrides.</p>
                <div className="mt-1 grid gap-1">{[...waves].sort(([left], [right]) => left.localeCompare(right)).map(([wave, count]) => <div key={wave}>{wave} ({count} updates)</div>)}</div>
                {rows.some((row) => row.systemEnforced) && <p>Required support updates continue on their schedule.</p>}
                {rows.some((row) => row.holdClearPending) && <p>Release hold clear pending</p>}
            </td>
            <td className="px-3 py-2">
                {canControl && <div className="flex flex-wrap gap-1">
                    {action("Start latest updates", "publishTenantMainRelease", startable.map(({ releaseId }) => ({ releaseId, waveMode: AUTOMATIC })))}
                    {[false, true].map((resume) => <span key={String(resume)}>{action(resume ? "Resume updates" : "Hold updates", deliveryControlCommand, holdable.filter((row) => deliveryStopped(row) === resume).flatMap((row) => deliveryControlCommands(row, resume)))}</span>)}
                    {action("Use automatic waves", "setWaveMode", manual.map(({ publicationId }) => ({ publicationId, waveMode: AUTOMATIC })))}
                    {action("Advance content waves", "advanceWave", manual.flatMap(waveAdvanceCommands), advancingDisabled)}
                </div>}
            </td>
        </tr>
    );
}
